import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileText, Calendar, Monitor, Plus, Edit, Trash2, Eye, EyeOff, 
  CheckCircle, XCircle, Clock, ArrowLeft, Save, ExternalLink, Upload,
  Image, File, Megaphone, Play, Pause, BarChart3, Package, MapPin, Flag, DollarSign, Link, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../../components/Header';
import api from '../../utils/api';
import { toFullUrl } from '../../utils/imageUrl';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

const AD_PRICING = {
  "1_day": { price: 25.00, label: "1 Day" },
  "1_week": { price: 100.00, label: "1 Week" },
  "1_month": { price: 350.00, label: "1 Month" },
  "3_months": { price: 900.00, label: "3 Months" }
};

const AdminCMS = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('news');
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [software, setSoftware] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // File upload refs
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const adImageRef = useRef(null);
  
  // Edit states
  const [editingNews, setEditingNews] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showAdForm, setShowAdForm] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Preview states
  const [previewNews, setPreviewNews] = useState(null);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [previewAd, setPreviewAd] = useState(null);
  
  // Form data
  const [newsForm, setNewsForm] = useState({
    title: '', content: '', excerpt: '', featured_image: '', tags: '', category: 'industry_news', published: false
  });
  const [eventForm, setEventForm] = useState({
    title: '', description: '', event_type: 'webinar', start_time: '', end_time: '',
    location: '', registration_url: '', max_attendees: ''
  });
  const [adForm, setAdForm] = useState({
    title: '', content: '', image_url: '', link_url: '', company_id: '', company_name: '',
    duration: '1_week', status: 'active', paid: true, priority: 1, admin_notes: '',
    target_pages: [], video_url: ''
  });
  
  // RFQ states
  const [showRfqForm, setShowRfqForm] = useState(false);
  const [editingRfq, setEditingRfq] = useState(null);
  const [rfqForm, setRfqForm] = useState({
    title: '', cargo_type: 'general', origin: '', destination: '',
    weight: '', volume: '', deadline: '', budget: '', description: '', status: 'open'
  });
  
  // Software states
  const [showSoftwareForm, setShowSoftwareForm] = useState(false);
  const [editingSoftware, setEditingSoftware] = useState(null);
  const [softwareForm, setSoftwareForm] = useState({
    name: '', tagline: '', description: '', category: 'freight_management',
    pricing: 'subscription', website: '', logo_url: '', features: ''
  });
  const [allSoftware, setAllSoftware] = useState([]);
  
  // Quote states for admin
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    title: '', origin: '', destination: '', cargo_type: 'general',
    transport_mode: 'sea', currency: 'USD', base_rate: '', transit_time: '',
    validity_days: '30', notes: '', status: 'published'
  });
  
  // URL Import states
  const [showUrlImport, setShowUrlImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importingUrl, setImportingUrl] = useState(false);

  useEffect(() => {
    fetchAllData();
    fetchAllSoftwareData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [newsRes, eventsRes, softwareRes, adsRes, rfqsRes, companiesRes, quotesRes] = await Promise.all([
        api.get('/admin/news'),
        api.get('/admin/events'),
        api.get('/software/pending'),
        api.get('/admin/advertisements'),
        api.get('/admin/rfqs'),
        api.get('/admin/companies').catch(() => ({ data: [] })),
        api.get('/admin/quotes').catch(() => ({ data: [] }))
      ]);
      setNews(newsRes.data);
      setEvents(eventsRes.data);
      setSoftware(softwareRes.data);
      setAdvertisements(adsRes.data);
      setRfqs(rfqsRes.data);
      setCompanies(companiesRes.data || []);
      setQuotes(quotesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAllSoftwareData = async () => {
    try {
      const res = await api.get('/admin/software');
      setAllSoftware(res.data || []);
    } catch (error) {
      console.error('Failed to fetch all software:', error);
    }
  };

  // File upload handler
  const handleFileUpload = async (newsId, file, fileType) => {
    if (!newsId) {
      toast.error('Please save the news post first before uploading files');
      return;
    }
    
    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const folder = fileType === 'image' ? 'news' : 'certifications';
      const result = await uploadToCloudinary(file, folder, token);
      
      // Create attachment object
      const attachment = {
        id: Date.now().toString(),
        url: result.url,
        filename: file.name,
        type: fileType,
        uploaded_at: new Date().toISOString()
      };
      
      // Update the news article with the new attachment
      const currentNews = news.find(n => n.id === newsId);
      if (currentNews) {
        const updatedAttachments = [...(currentNews.attachments || []), attachment];
        await api.put(`/admin/news/${newsId}`, {
          ...currentNews,
          attachments: updatedAttachments,
          // If this is an image and there's no featured image, set it
          featured_image: (!currentNews.featured_image && fileType === 'image') 
            ? result.url 
            : currentNews.featured_image
        });
        
        if (!currentNews.featured_image && fileType === 'image') {
          toast.success('Image uploaded and set as featured image');
        } else {
          toast.success(`${fileType === 'image' ? 'Image' : 'Document'} uploaded to cloud storage`);
        }
      }
      
      fetchAllData();
      return attachment;
    } catch (error) {
      toast.error(`Failed to upload ${fileType}: ${error.message}`);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // Set image as featured image
  const setAsFeaturedImage = async (newsId, imageUrl) => {
    try {
      const currentNews = news.find(n => n.id === newsId);
      if (currentNews) {
        await api.put(`/admin/news/${newsId}`, {
          ...currentNews,
          featured_image: imageUrl
        });
        toast.success('Featured image updated');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to set featured image');
    }
  };

  const handleDeleteAttachment = async (newsId, attachmentId) => {
    try {
      await api.delete(`/admin/news/${newsId}/attachment/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  // Import news from URL
  const handleImportFromUrl = async () => {
    if (!importUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    
    setImportingUrl(true);
    try {
      const response = await api.post('/admin/news/from-url', { url: importUrl });
      const data = response.data;
      
      // Build content with image references
      let contentWithImages = data.content || '';
      
      // Add image gallery section if there are multiple images
      if (data.all_images && data.all_images.length > 1) {
        contentWithImages += '\n\n---\n\n**Article Images:**\n';
        data.all_images.forEach((img, index) => {
          if (img.type === 'content') {
            contentWithImages += `\n![${img.alt || 'Image ' + (index + 1)}](${img.url})\n`;
          }
        });
      }
      
      // Add source URL
      if (data.source_url) {
        contentWithImages += `\n\n---\n**Source:** ${data.source_url}`;
      }
      
      // Pre-fill the news form with extracted data
      setNewsForm({
        title: data.title || '',
        content: contentWithImages,
        excerpt: data.excerpt || '',
        featured_image: data.featured_image || '',
        tags: '',
        category: 'industry_news',
        published: false
      });
      
      setShowUrlImport(false);
      setImportUrl('');
      setShowNewsForm(true);
      
      const imageCount = data.images_count || 0;
      toast.success(`Content extracted! Found ${imageCount} image(s). Review and edit before publishing.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract content from URL');
    } finally {
      setImportingUrl(false);
    }
  };

  // News functions
  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newsForm,
        tags: newsForm.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      if (editingNews) {
        await api.put(`/admin/news/${editingNews.id}`, payload);
        toast.success('News post updated');
      } else {
        await api.post('/admin/news', payload);
        toast.success('News post created');
      }
      
      setShowNewsForm(false);
      setEditingNews(null);
      setNewsForm({ title: '', content: '', excerpt: '', featured_image: '', tags: '', category: 'industry_news', published: false });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save news post');
    }
  };

  const editNews = (item) => {
    setEditingNews(item);
    setNewsForm({
      title: item.title,
      content: item.content,
      excerpt: item.excerpt || '',
      featured_image: item.featured_image || '',
      tags: item.tags?.join(', ') || '',
      category: item.category || 'industry_news',
      published: item.published
    });
    setShowNewsForm(true);
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news post?')) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success('News post deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete news post');
    }
  };

  const toggleNewsPublish = async (item) => {
    try {
      await api.put(`/admin/news/${item.id}`, { published: !item.published });
      toast.success(item.published ? 'News unpublished' : 'News published');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update news');
    }
  };

  // Event functions
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...eventForm,
        max_attendees: eventForm.max_attendees ? parseInt(eventForm.max_attendees) : null
      };
      
      if (editingEvent) {
        await api.put(`/admin/events/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/admin/events', payload);
        toast.success('Event created');
      }
      
      setShowEventForm(false);
      setEditingEvent(null);
      setEventForm({
        title: '', description: '', event_type: 'webinar', start_time: '', end_time: '',
        location: '', registration_url: '', max_attendees: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const editEvent = (item) => {
    setEditingEvent(item);
    setEventForm({
      title: item.title,
      description: item.description,
      event_type: item.event_type,
      start_time: item.start_time?.slice(0, 16) || '',
      end_time: item.end_time?.slice(0, 16) || '',
      location: item.location || '',
      registration_url: item.registration_url || '',
      max_attendees: item.max_attendees?.toString() || ''
    });
    setShowEventForm(true);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success('Event deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Software moderation
  const approveSoftware = async (id) => {
    try {
      await api.post(`/software/${id}/approve`);
      toast.success('Software approved');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve software');
    }
  };

  const rejectSoftware = async (id) => {
    const notes = window.prompt('Rejection reason (optional):');
    try {
      await api.post(`/software/${id}/reject?notes=${encodeURIComponent(notes || '')}`);
      toast.success('Software rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject software');
    }
  };

  // Advertisement management
  const approveAd = async (id) => {
    try {
      await api.put(`/admin/advertisements/${id}/approve`);
      toast.success('Advertisement approved and activated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve advertisement');
    }
  };

  const rejectAd = async (id) => {
    const reason = window.prompt('Rejection reason (optional):');
    try {
      await api.put(`/admin/advertisements/${id}/reject?reason=${encodeURIComponent(reason || '')}`);
      toast.success('Advertisement rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject advertisement');
    }
  };

  const pauseAd = async (id) => {
    try {
      await api.put(`/admin/advertisements/${id}/pause`);
      toast.success('Advertisement paused');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to pause advertisement');
    }
  };

  const activateAd = async (id) => {
    try {
      await api.put(`/admin/advertisements/${id}`, { status: 'active' });
      toast.success('Advertisement activated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to activate advertisement');
    }
  };

  const deleteAd = async (id) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;
    try {
      await api.delete(`/admin/advertisements/${id}`);
      toast.success('Advertisement deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete advertisement');
    }
  };

  const handleAdSubmit = async (e) => {
    e.preventDefault();
    try {
      const adData = {
        ...adForm,
        priority: parseInt(adForm.priority) || 1
      };
      
      if (editingAd) {
        await api.put(`/admin/advertisements/${editingAd.id}`, adData);
        toast.success('Advertisement updated');
      } else {
        await api.post('/admin/advertisements', adData);
        toast.success('Advertisement created');
      }
      setShowAdForm(false);
      setEditingAd(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save advertisement');
    }
  };

  const editAd = (ad) => {
    setEditingAd(ad);
    setAdForm({
      title: ad.title || '',
      content: ad.content || '',
      image_url: ad.image_url || '',
      link_url: ad.link_url || '',
      company_id: ad.company_id || '',
      company_name: ad.company_name || '',
      duration: ad.duration || '1_week',
      status: ad.status || 'active',
      paid: ad.paid !== false,
      priority: ad.priority || 1,
      admin_notes: ad.admin_notes || '',
      target_pages: ad.target_pages || [],
      video_url: ad.video_url || ''
    });
    setShowAdForm(true);
  };

  const handleAdImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const result = await uploadToCloudinary(file, 'ads', token);
      setAdForm(prev => ({ ...prev, image_url: result.url }));
      toast.success('Image uploaded to cloud storage');
    } catch (error) {
      toast.error('Failed to upload image: ' + error.message);
    } finally {
      setUploadingFile(false);
    }
  };

  // RFQ Management Functions
  const handleRfqSubmit = async (e) => {
    e.preventDefault();
    try {
      const rfqData = {
        ...rfqForm,
        weight: rfqForm.weight ? parseFloat(rfqForm.weight) : null,
        volume: rfqForm.volume ? parseFloat(rfqForm.volume) : null,
        budget: rfqForm.budget ? parseFloat(rfqForm.budget) : null
      };
      
      if (editingRfq) {
        await api.put(`/admin/rfqs/${editingRfq.id}`, rfqData);
        toast.success('RFQ updated');
      } else {
        await api.post('/admin/rfqs', rfqData);
        toast.success('RFQ created');
      }
      setShowRfqForm(false);
      setEditingRfq(null);
      setRfqForm({
        title: '', cargo_type: 'general', origin: '', destination: '',
        weight: '', volume: '', deadline: '', budget: '', description: '', status: 'open'
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save RFQ');
    }
  };
  
  const editRfq = (rfq) => {
    setEditingRfq(rfq);
    setRfqForm({
      title: rfq.title || '',
      cargo_type: rfq.cargo_type || 'general',
      origin: rfq.origin || '',
      destination: rfq.destination || '',
      weight: rfq.weight?.toString() || '',
      volume: rfq.volume?.toString() || '',
      deadline: rfq.deadline ? new Date(rfq.deadline).toISOString().split('T')[0] : '',
      budget: rfq.budget?.toString() || '',
      description: rfq.description || '',
      status: rfq.status || 'open'
    });
    setShowRfqForm(true);
  };
  
  // Software Management Functions
  const fetchAllSoftware = async () => {
    try {
      const response = await api.get('/admin/software');
      setAllSoftware(response.data);
    } catch (error) {
      console.error('Failed to fetch software:', error);
    }
  };
  
  const handleSoftwareSubmit = async (e) => {
    e.preventDefault();
    try {
      const softwareData = {
        ...softwareForm,
        features: softwareForm.features.split('\n').filter(f => f.trim())
      };
      
      if (editingSoftware) {
        await api.put(`/admin/software/${editingSoftware.id}`, softwareData);
        toast.success('Software updated');
      } else {
        await api.post('/admin/software', softwareData);
        toast.success('Software added');
      }
      setShowSoftwareForm(false);
      setEditingSoftware(null);
      setSoftwareForm({
        name: '', tagline: '', description: '', category: 'freight_management',
        pricing: 'subscription', website: '', logo_url: '', features: ''
      });
      fetchAllData();
      fetchAllSoftware();
    } catch (error) {
      toast.error('Failed to save software');
    }
  };
  
  const editSoftware = (sw) => {
    setEditingSoftware(sw);
    setSoftwareForm({
      name: sw.name || '',
      tagline: sw.tagline || '',
      description: sw.description || '',
      category: sw.category || 'freight_management',
      pricing: sw.pricing || 'subscription',
      website: sw.website || '',
      logo_url: sw.logo_url || '',
      features: Array.isArray(sw.features) ? sw.features.join('\n') : ''
    });
    setShowSoftwareForm(true);
  };
  
  const deleteSoftware = async (id) => {
    if (!window.confirm('Are you sure you want to delete this software?')) return;
    try {
      await api.delete(`/admin/software/${id}`);
      toast.success('Software deleted');
      fetchAllData();
      fetchAllSoftwareData();
    } catch (error) {
      toast.error('Failed to delete software');
    }
  };

  // Quote Management Functions
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const quoteData = {
        ...quoteForm,
        base_rate: parseFloat(quoteForm.base_rate) || 0,
        total_price: parseFloat(quoteForm.base_rate) || 0,
        validity_days: parseInt(quoteForm.validity_days) || 30
      };
      
      if (editingQuote) {
        await api.put(`/admin/quotes/${editingQuote.id}`, quoteData);
        toast.success('Quote updated');
      } else {
        await api.post('/admin/quotes', quoteData);
        toast.success('Quote created');
      }
      setShowQuoteForm(false);
      setEditingQuote(null);
      setQuoteForm({
        title: '', origin: '', destination: '', cargo_type: 'general',
        transport_mode: 'sea', currency: 'USD', base_rate: '', transit_time: '',
        validity_days: '30', notes: '', status: 'published'
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save quote');
    }
  };
  
  const editQuote = (quote) => {
    setEditingQuote(quote);
    setQuoteForm({
      title: quote.title || '',
      origin: quote.origin || '',
      destination: quote.destination || '',
      cargo_type: quote.cargo_type || 'general',
      transport_mode: quote.transport_mode || 'sea',
      currency: quote.currency || 'USD',
      base_rate: quote.base_rate?.toString() || quote.total_price?.toString() || '',
      transit_time: quote.transit_time || '',
      validity_days: quote.validity_days?.toString() || '30',
      notes: quote.notes || '',
      status: quote.status || 'published'
    });
    setShowQuoteForm(true);
  };
  
  const deleteQuote = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      toast.success('Quote deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const updateRfqStatus = async (rfqId, status) => {
    try {
      await api.put(`/admin/rfqs/${rfqId}/status`, { status });
      toast.success(`RFQ status updated to ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update RFQ status');
    }
  };

  const deleteRfq = async (id) => {
    if (!window.confirm('Are you sure you want to delete this RFQ? This will also delete all associated quotes.')) return;
    try {
      await api.delete(`/admin/rfqs/${id}`);
      toast.success('RFQ deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete RFQ');
    }
  };

  const getRfqStatusBadge = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-700',
      closed: 'bg-slate-100 text-slate-700',
      awarded: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      flagged: 'bg-orange-100 text-orange-700'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const getAdStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      paused: 'bg-slate-100 text-slate-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-500'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="text-sm">
              <ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Admin</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
          
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-bold text-ocean-900">Content Management</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">Manage news, events, software, and ads</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4 sm:mb-6 flex flex-wrap gap-1 h-auto p-1 bg-slate-100 rounded-lg">
              <TabsTrigger value="news" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
                <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">News</span> ({news.length})
              </TabsTrigger>
              <TabsTrigger value="events" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Events</span> ({events.length})
              </TabsTrigger>
              <TabsTrigger value="rfqs" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
                <Package className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">RFQs</span> ({rfqs.length})
              </TabsTrigger>
              <TabsTrigger value="quotes" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
                <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Quotes</span> ({quotes.length})
              </TabsTrigger>
              <TabsTrigger value="software" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
                <Monitor className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Software</span> ({software.length})
              </TabsTrigger>
              <TabsTrigger value="ads" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
                <Megaphone className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Ads</span> ({advertisements.filter(a => a.status === 'pending').length})
              </TabsTrigger>
            </TabsList>

            {/* NEWS TAB */}
            <TabsContent value="news">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
                <h2 className="text-lg sm:text-xl font-semibold">News Posts</h2>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button 
                    onClick={() => setShowUrlImport(true)}
                    variant="outline"
                    className="flex-1 sm:flex-none text-sm"
                    data-testid="import-url-btn"
                  >
                    <Link className="h-4 w-4 mr-2" />
                    Import from URL
                  </Button>
                  <Button 
                    onClick={() => {
                      setEditingNews(null);
                      setNewsForm({ title: '', content: '', excerpt: '', featured_image: '', tags: '', category: 'industry_news', published: false });
                      setShowNewsForm(true);
                    }}
                    className="bg-cyan-500 hover:bg-cyan-600 flex-1 sm:flex-none text-sm"
                    data-testid="create-news-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create News
                  </Button>
                </div>
              </div>

              {/* URL Import Dialog */}
              <Dialog open={showUrlImport} onOpenChange={setShowUrlImport}>
                <DialogContent className="w-[95vw] max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Link className="h-5 w-5 text-cyan-500" />
                      Import Article from URL
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <p className="text-sm text-slate-600">
                      Paste a URL to automatically extract the article title, image, and content.
                    </p>
                    <div className="space-y-2">
                      <Label>Article URL</Label>
                      <Input
                        value={importUrl}
                        onChange={(e) => setImportUrl(e.target.value)}
                        placeholder="https://example.com/article..."
                        disabled={importingUrl}
                        data-testid="import-url-input"
                      />
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <p className="text-amber-800 font-medium mb-1">Note:</p>
                      <p className="text-amber-700">Some websites block automated extraction. If import fails, you can manually copy/paste the article content using the "Add News" button instead.</p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowUrlImport(false);
                          setImportUrl('');
                        }}
                        disabled={importingUrl}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleImportFromUrl}
                        className="bg-cyan-500 hover:bg-cyan-600"
                        disabled={importingUrl || !importUrl.trim()}
                        data-testid="import-url-submit"
                      >
                        {importingUrl ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Extract Content
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* News Form Dialog */}
              <Dialog open={showNewsForm} onOpenChange={setShowNewsForm}>
                <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader>
                    <DialogTitle className="text-base sm:text-lg">{editingNews ? 'Edit News Post' : 'Create News Post'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleNewsSubmit} className="space-y-3 sm:space-y-4 pt-3 sm:pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1 sm:space-y-2">
                        <Label className="text-sm">Title *</Label>
                        <Input
                          value={newsForm.title}
                          onChange={(e) => setNewsForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="News headline"
                          required
                          className="text-sm"
                          data-testid="news-title"
                        />
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <Label className="text-sm">Category</Label>
                        <Select value={newsForm.category} onValueChange={(v) => setNewsForm(prev => ({ ...prev, category: v }))}>
                          <SelectTrigger className="text-sm">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="industry_news">Industry News</SelectItem>
                            <SelectItem value="company_update">Company Update</SelectItem>
                            <SelectItem value="announcement">Announcement</SelectItem>
                            <SelectItem value="resource">Resource/Guide</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Excerpt (Short Summary)</Label>
                      <Input
                        value={newsForm.excerpt}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, excerpt: e.target.value }))}
                        placeholder="Brief description for article preview"
                        data-testid="news-excerpt"
                      />
                    </div>
                    
                    {/* Featured Image Section */}
                    <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                      <Label className="text-sm font-semibold flex items-center gap-2">
                        <Image className="h-4 w-4 text-cyan-500" />
                        Featured Image
                      </Label>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                          <Input
                            value={newsForm.featured_image}
                            onChange={(e) => setNewsForm(prev => ({ ...prev, featured_image: e.target.value }))}
                            placeholder="Paste image URL or upload below"
                            data-testid="news-image"
                          />
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            id="featured-image-upload"
                            onChange={async (e) => {
                              if (e.target.files[0]) {
                                setUploadingFile(true);
                                try {
                                  const token = localStorage.getItem('token');
                                  const result = await uploadToCloudinary(e.target.files[0], 'news', token);
                                  setNewsForm(prev => ({ ...prev, featured_image: result.url }));
                                  toast.success('Featured image uploaded');
                                } catch (error) {
                                  toast.error('Failed to upload image');
                                } finally {
                                  setUploadingFile(false);
                                }
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => document.getElementById('featured-image-upload').click()}
                            disabled={uploadingFile}
                          >
                            {uploadingFile ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      {newsForm.featured_image && (
                        <div className="mt-2">
                          <img 
                            src={newsForm.featured_image} 
                            alt="Featured preview" 
                            className="w-full max-w-md h-40 object-cover rounded border"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Content Editor Section */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">Content *</Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const selection = window.getSelection().toString();
                              if (selection) {
                                const url = window.prompt('Enter link URL:');
                                if (url) {
                                  const linkHtml = `<a href="${url}" target="_blank" class="text-cyan-600 hover:underline">${selection}</a>`;
                                  setNewsForm(prev => ({
                                    ...prev,
                                    content: prev.content.replace(selection, linkHtml)
                                  }));
                                }
                              } else {
                                toast.info('Select some text first to add a link');
                              }
                            }}
                            title="Add link to selected text"
                          >
                            <Link className="h-4 w-4 mr-1" />
                            Add Link
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const url = window.prompt('Enter image URL to insert:');
                              if (url) {
                                const imageHtml = `\n<figure><img src="${url}" alt="Article image" class="w-full rounded-lg"></figure>\n`;
                                setNewsForm(prev => ({
                                  ...prev,
                                  content: prev.content + imageHtml
                                }));
                              }
                            }}
                            title="Insert image in content"
                          >
                            <Image className="h-4 w-4 mr-1" />
                            Insert Image
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 mb-1">
                        Supports HTML: Use &lt;p&gt;, &lt;h2&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a&gt;, &lt;img&gt;, &lt;blockquote&gt; tags
                      </div>
                      <Textarea
                        value={newsForm.content}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="Full article content... You can use HTML tags for formatting."
                        rows={12}
                        required
                        className="font-mono text-sm"
                        data-testid="news-content"
                      />
                    </div>
                    
                    {/* Content Preview */}
                    {newsForm.content && (
                      <div className="border rounded-lg p-4 bg-white">
                        <Label className="text-sm font-semibold text-slate-600 mb-2 block">Content Preview</Label>
                        <div 
                          className="prose prose-sm max-w-none prose-p:mb-3 prose-ul:list-disc prose-ul:pl-6 prose-li:my-1"
                          dangerouslySetInnerHTML={{ __html: newsForm.content }}
                        />
                      </div>
                    )}
                    
                    {/* Additional Media - Only show when editing existing article */}
                    {editingNews && (
                      <div className="border rounded-lg p-4 space-y-4 bg-blue-50">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Image className="h-5 w-5 text-blue-500" />
                          Additional Images & Attachments
                        </Label>
                        <p className="text-xs text-slate-600">
                          Upload additional images or documents. Click an image to set it as the featured image.
                        </p>
                        
                        {/* Image Upload */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => imageInputRef.current?.click()}
                              disabled={uploadingFile}
                            >
                              <Image className="h-4 w-4 mr-2" />
                              Upload Image
                            </Button>
                            <input
                              ref={imageInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileUpload(editingNews.id, e.target.files[0], 'image');
                                }
                              }}
                            />
                            <span className="text-xs text-slate-500">JPEG, PNG, GIF, WebP (max 10MB)</span>
                          </div>
                          {editingNews.images?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-500 mb-2">Click an image to set it as featured:</p>
                              <div className="flex flex-wrap gap-2">
                                {editingNews.images.map((img) => (
                                  <div key={img.id} className="relative group">
                                    <img 
                                      src={toFullUrl(img.url)} 
                                      alt="" 
                                      className={`w-20 h-20 object-cover rounded cursor-pointer border-2 transition-all ${
                                        newsForm.featured_image === img.url 
                                          ? 'border-cyan-500 ring-2 ring-cyan-200' 
                                          : 'border-transparent hover:border-cyan-300'
                                      }`}
                                      onClick={() => {
                                        setNewsForm(prev => ({ ...prev, featured_image: img.url }));
                                        toast.success('Image set as featured');
                                      }}
                                      title="Click to set as featured image"
                                    />
                                    {newsForm.featured_image === img.url && (
                                      <div className="absolute -top-1 -left-1 bg-cyan-500 text-white text-[8px] px-1 rounded">
                                        Featured
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteAttachment(editingNews.id, img.id);
                                      }}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* PDF/Document Upload */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => pdfInputRef.current?.click()}
                              disabled={uploadingFile}
                            >
                              <File className="h-4 w-4 mr-2" />
                              Upload Document
                            </Button>
                            <input
                              ref={pdfInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileUpload(editingNews.id, e.target.files[0], 'pdf');
                                }
                              }}
                            />
                            <span className="text-xs text-slate-500">PDF, DOC, DOCX</span>
                          </div>
                          {editingNews.attachments?.filter(a => a.type !== 'image').length > 0 && (
                            <div className="space-y-1 mt-2">
                              {editingNews.attachments.filter(a => a.type !== 'image').map((att) => (
                                <div key={att.id} className="flex items-center gap-2 text-sm bg-white rounded px-2 py-1">
                                  <File className="h-4 w-4 text-red-500" />
                                  <a href={toFullUrl(att.url)} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline flex-1">
                                    {att.filename}
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteAttachment(editingNews.id, att.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {uploadingFile && (
                          <div className="text-sm text-cyan-600 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading to cloud storage...
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Tags (comma-separated)</Label>
                      <Input
                        value={newsForm.tags}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, tags: e.target.value }))}
                        placeholder="Industry, Trade, Caribbean"
                        data-testid="news-tags"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="published"
                        checked={newsForm.published}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, published: e.target.checked }))}
                        className="rounded"
                      />
                      <Label htmlFor="published">Publish immediately</Label>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowNewsForm(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" data-testid="save-news-btn">
                        <Save className="h-4 w-4 mr-2" />
                        {editingNews ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* News List */}
              <div className="space-y-4">
                {news.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-slate-500">
                      No news posts yet. Create your first one!
                    </CardContent>
                  </Card>
                ) : (
                  news.map((item) => (
                    <Card key={item.id}>
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setPreviewNews(item)}>
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                              <h3 className="font-semibold text-sm sm:text-base hover:text-cyan-600 transition-colors truncate">{item.title}</h3>
                              <Badge variant={item.published ? 'default' : 'secondary'} className="text-xs">
                                {item.published ? 'Published' : 'Draft'}
                              </Badge>
                            </div>
                            <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">{item.excerpt || item.content}</p>
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-2 text-[10px] sm:text-xs text-slate-400">
                              <Clock className="h-3 w-3" />
                              {new Date(item.created_at).toLocaleDateString()}
                              {item.tags?.length > 0 && (
                                <span className="truncate max-w-[100px] sm:max-w-none">Tags: {item.tags.join(', ')}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            {item.published && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`/news/${item.id}`, '_blank')}
                                title="View Full Article"
                                className="text-green-600 h-8 w-8 sm:h-9 sm:w-9 p-0"
                              >
                                <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPreviewNews(item)}
                              title="Preview"
                              className="text-cyan-600 h-8 w-8 sm:h-9 sm:w-9 p-0"
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleNewsPublish(item)}
                              title={item.published ? 'Unpublish' : 'Publish'}
                              className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                            >
                              {item.published ? <EyeOff className="h-3 w-3 sm:h-4 sm:w-4" /> : <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => editNews(item)} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                              <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 sm:h-9 sm:w-9 p-0" onClick={() => deleteNews(item.id)}>
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* EVENTS TAB */}
            <TabsContent value="events">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Events</h2>
                <Button 
                  onClick={() => {
                    setEditingEvent(null);
                    setEventForm({
                      title: '', description: '', event_type: 'webinar', start_time: '', end_time: '',
                      location: '', registration_url: '', max_attendees: ''
                    });
                    setShowEventForm(true);
                  }}
                  className="bg-cyan-500 hover:bg-cyan-600"
                  data-testid="create-event-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Event
                </Button>
              </div>

              {/* Event Form Dialog */}
              <Dialog open={showEventForm} onOpenChange={setShowEventForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleEventSubmit} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={eventForm.title}
                        onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Event title"
                        required
                        data-testid="event-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Textarea
                        value={eventForm.description}
                        onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Event description..."
                        rows={4}
                        required
                        data-testid="event-description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Event Type *</Label>
                        <Select
                          value={eventForm.event_type}
                          onValueChange={(value) => setEventForm(prev => ({ ...prev, event_type: value }))}
                        >
                          <SelectTrigger data-testid="event-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="conference">Conference</SelectItem>
                            <SelectItem value="webinar">Webinar</SelectItem>
                            <SelectItem value="workshop">Workshop</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input
                          value={eventForm.location}
                          onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Virtual or physical location"
                          data-testid="event-location"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Time *</Label>
                        <Input
                          type="datetime-local"
                          value={eventForm.start_time}
                          onChange={(e) => setEventForm(prev => ({ ...prev, start_time: e.target.value }))}
                          required
                          data-testid="event-start"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Time *</Label>
                        <Input
                          type="datetime-local"
                          value={eventForm.end_time}
                          onChange={(e) => setEventForm(prev => ({ ...prev, end_time: e.target.value }))}
                          required
                          data-testid="event-end"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Registration URL</Label>
                        <Input
                          value={eventForm.registration_url}
                          onChange={(e) => setEventForm(prev => ({ ...prev, registration_url: e.target.value }))}
                          placeholder="https://..."
                          data-testid="event-registration-url"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Attendees</Label>
                        <Input
                          type="number"
                          value={eventForm.max_attendees}
                          onChange={(e) => setEventForm(prev => ({ ...prev, max_attendees: e.target.value }))}
                          placeholder="100"
                          data-testid="event-max-attendees"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowEventForm(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" data-testid="save-event-btn">
                        <Save className="h-4 w-4 mr-2" />
                        {editingEvent ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Events List */}
              <div className="space-y-4">
                {events.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-slate-500">
                      No events yet. Create your first one!
                    </CardContent>
                  </Card>
                ) : (
                  events.map((item) => (
                    <Card key={item.id}>
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                              <h3 className="font-semibold text-sm sm:text-base truncate">{item.title}</h3>
                              <Badge variant="outline" className="capitalize text-xs">{item.event_type}</Badge>
                            </div>
                            <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">{item.description}</p>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-[10px] sm:text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(item.start_time).toLocaleString()}
                              </span>
                              {item.location && <span className="truncate max-w-[100px] sm:max-w-none">{item.location}</span>}
                              {item.max_attendees && (
                                <span>{item.registered_count || 0}/{item.max_attendees}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => setPreviewEvent(item)}
                              title="Preview"
                              className="text-cyan-600 h-8 w-8 sm:h-9 sm:w-9 p-0"
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => editEvent(item)} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                              <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 sm:h-9 sm:w-9 p-0" onClick={() => deleteEvent(item.id)}>
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* SOFTWARE TAB */}
            <TabsContent value="software">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Software Management</h2>
                  <p className="text-sm text-slate-600">Add, edit, and manage software listings in the marketplace</p>
                </div>
                <Button
                  onClick={() => {
                    setEditingSoftware(null);
                    setSoftwareForm({
                      name: '', tagline: '', description: '', category: 'freight_management',
                      pricing: 'subscription', website: '', logo_url: '', features: ''
                    });
                    setShowSoftwareForm(true);
                  }}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Software
                </Button>
              </div>

              {/* Pending Approvals Section */}
              {software.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-orange-700">Pending Approvals ({software.length})</h3>
                  <div className="space-y-3">
                    {software.map((item) => (
                      <Card key={item.id} className="border-orange-200 bg-orange-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {item.logo_url && (
                              <img
                                src={toFullUrl(item.logo_url)}
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{item.name}</h3>
                                <Badge variant="outline">{item.category}</Badge>
                                <Badge className="bg-orange-100 text-orange-700">Pending</Badge>
                              </div>
                              <p className="text-sm text-slate-500">{item.tagline}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => approveSoftware(item.id)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-500"
                                onClick={() => rejectSoftware(item.id)}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* All Software Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">All Software Listings</h3>
                <div className="space-y-3">
                  {allSoftware.length === 0 ? (
                    <Card>
                      <CardContent className="p-8 text-center text-slate-500">
                        <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        No software listings yet. Click "Add Software" to create one.
                      </CardContent>
                    </Card>
                  ) : (
                    allSoftware.map((item) => (
                      <Card key={item.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {item.logo_url && (
                              <img
                                src={toFullUrl(item.logo_url)}
                                alt={item.name}
                                className="w-16 h-16 rounded-lg object-cover"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{item.name}</h3>
                                <Badge variant="outline">{item.category?.replace('_', ' ')}</Badge>
                                <Badge variant="secondary">{item.pricing?.replace('_', ' ')}</Badge>
                                <Badge className={
                                  item.status === 'active' ? 'bg-green-100 text-green-700' :
                                  item.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                  'bg-red-100 text-red-700'
                                }>
                                  {item.status}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-500">{item.tagline}</p>
                              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{item.description}</p>
                              {item.website && (
                                <a
                                  href={item.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-cyan-600 hover:underline mt-2 inline-flex items-center gap-1"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  Visit Website
                                </a>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => editSoftware(item)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500"
                                onClick={() => deleteSoftware(item.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ADVERTISEMENTS TAB */}
            <TabsContent value="ads">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Advertisement Management</h2>
                  <p className="text-sm text-slate-600">Create, review, and manage company advertisements</p>
                </div>
                <Button
                  onClick={() => {
                    setEditingAd(null);
                    setAdForm({
                      title: '', content: '', image_url: '', link_url: '', company_id: '', company_name: '',
                      duration: '1_week', status: 'active', paid: true, priority: 1, admin_notes: '',
                      target_pages: [], video_url: ''
                    });
                    setShowAdForm(true);
                  }}
                  className="bg-cyan-500 hover:bg-cyan-600"
                  data-testid="create-ad-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Ad
                </Button>
              </div>

              {/* Pricing Info Card */}
              <Card className="mb-4 bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-cyan-600" />
                    <h3 className="font-semibold text-cyan-900">Ad Pricing Structure</h3>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    {Object.entries(AD_PRICING).map(([key, { price, label }]) => (
                      <div key={key} className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <p className="font-semibold text-cyan-700">{label}</p>
                        <p className="text-lg font-bold text-ocean-900">${price}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Ad Form Dialog */}
              <Dialog open={showAdForm} onOpenChange={setShowAdForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingAd ? 'Edit Advertisement' : 'Create Advertisement'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAdSubmit} className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Input
                          value={adForm.title}
                          onChange={(e) => setAdForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Ad headline"
                          required
                          data-testid="ad-title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Company</Label>
                        <Select
                          value={adForm.company_id}
                          onValueChange={(v) => {
                            const company = companies.find(c => c.id === v);
                            setAdForm(prev => ({ 
                              ...prev, 
                              company_id: v, 
                              company_name: company?.legal_name || ''
                            }));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select company (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ctc_admin">CTC Cargo Network</SelectItem>
                            {companies.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Ad Content</Label>
                      <Textarea
                        value={adForm.content}
                        onChange={(e) => setAdForm(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="Ad description or promotional text..."
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Duration *</Label>
                        <Select
                          value={adForm.duration}
                          onValueChange={(v) => setAdForm(prev => ({ ...prev, duration: v }))}
                        >
                          <SelectTrigger data-testid="ad-duration">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1_day">1 Day ($25)</SelectItem>
                            <SelectItem value="1_week">1 Week ($100)</SelectItem>
                            <SelectItem value="1_month">1 Month ($350)</SelectItem>
                            <SelectItem value="3_months">3 Months ($900)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={adForm.status}
                          onValueChange={(v) => setAdForm(prev => ({ ...prev, status: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active (Live Now)</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                            <SelectItem value="pending">Pending Review</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Ad Image</Label>
                      <div className="flex items-center gap-3">
                        {adForm.image_url && (
                          <img src={toFullUrl(adForm.image_url)} alt="Ad" className="w-24 h-16 object-cover rounded" />
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => adImageRef.current?.click()}
                          disabled={uploadingFile}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingFile ? 'Uploading...' : 'Upload Image'}
                        </Button>
                        <input
                          ref={adImageRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleAdImageUpload}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Link URL</Label>
                      <Input
                        value={adForm.link_url}
                        onChange={(e) => setAdForm(prev => ({ ...prev, link_url: e.target.value }))}
                        placeholder="https://example.com/landing-page"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Priority (1-10)</Label>
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={adForm.priority}
                          onChange={(e) => setAdForm(prev => ({ ...prev, priority: e.target.value }))}
                          placeholder="1"
                        />
                        <p className="text-xs text-slate-500">Higher priority = more frequent display</p>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          id="paid"
                          checked={adForm.paid}
                          onChange={(e) => setAdForm(prev => ({ ...prev, paid: e.target.checked }))}
                          className="rounded"
                        />
                        <Label htmlFor="paid">Paid</Label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Admin Notes</Label>
                      <Textarea
                        value={adForm.admin_notes}
                        onChange={(e) => setAdForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                        placeholder="Internal notes about this ad..."
                        rows={2}
                      />
                    </div>

                    {/* Target Pages Selection */}
                    <div className="space-y-2">
                      <Label>Target Pages (where ad will show)</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'all', label: 'All Pages' },
                          { value: 'dashboard', label: 'Dashboard' },
                          { value: 'directory', label: 'Directory' },
                          { value: 'rfqs', label: 'RFQ Marketplace' },
                          { value: 'quotes', label: 'Quotes' },
                          { value: 'news', label: 'News' },
                          { value: 'events', label: 'Events' },
                          { value: 'insights', label: 'Freight Insights' },
                          { value: 'software', label: 'Software' }
                        ].map(page => (
                          <label key={page.value} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={adForm.target_pages?.includes(page.value) || adForm.target_pages?.includes('all')}
                              onChange={(e) => {
                                if (page.value === 'all') {
                                  setAdForm(prev => ({
                                    ...prev,
                                    target_pages: e.target.checked ? ['all'] : []
                                  }));
                                } else {
                                  setAdForm(prev => ({
                                    ...prev,
                                    target_pages: e.target.checked
                                      ? [...(prev.target_pages || []).filter(p => p !== 'all'), page.value]
                                      : (prev.target_pages || []).filter(p => p !== page.value)
                                  }));
                                }
                              }}
                              className="rounded"
                            />
                            {page.label}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500">Select "All Pages" or specific pages where this ad should appear</p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowAdForm(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" data-testid="save-ad-btn">
                        <Save className="h-4 w-4 mr-2" />
                        {editingAd ? 'Update Ad' : 'Create Ad'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <div className="grid gap-4">
                {advertisements.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-slate-500">
                      <Megaphone className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      No advertisements yet. Create your first one!
                    </CardContent>
                  </Card>
                ) : (
                  advertisements.map((ad) => (
                    <Card key={ad.id} className={ad.status === 'pending' ? 'border-yellow-300' : ad.status === 'active' ? 'border-green-300' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {ad.image_url && (
                            <img
                              src={toFullUrl(ad.image_url)}
                              alt={ad.title}
                              className="w-32 h-24 rounded-lg object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{ad.title}</h3>
                              {getAdStatusBadge(ad.status)}
                              <Badge variant="outline" className="capitalize">{ad.ad_type}</Badge>
                              {ad.duration && (
                                <Badge variant="secondary">{AD_PRICING[ad.duration]?.label || ad.duration}</Badge>
                              )}
                              {ad.created_by_admin && (
                                <Badge className="bg-purple-100 text-purple-700">Admin Created</Badge>
                              )}
                            </div>
                            <p className="text-sm text-cyan-600">{ad.company_name}</p>
                            {ad.content && (
                              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{ad.content}</p>
                            )}
                            {ad.link_url && (
                              <a
                                href={ad.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-cyan-600 hover:underline mt-2 inline-flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {ad.link_url}
                              </a>
                            )}
                            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                {ad.impressions || 0} impressions
                              </span>
                              <span className="flex items-center gap-1">
                                <BarChart3 className="h-3 w-3" />
                                {ad.clicks || 0} clicks
                              </span>
                              {ad.price > 0 && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="h-3 w-3" />
                                  ${ad.price} {ad.paid ? '(Paid)' : '(Unpaid)'}
                                </span>
                              )}
                              {ad.start_date && (
                                <span>Starts: {new Date(ad.start_date).toLocaleDateString()}</span>
                              )}
                              {ad.end_date && (
                                <span>Ends: {new Date(ad.end_date).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPreviewAd(ad)}
                              className="text-cyan-600"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => editAd(ad)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            {ad.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600"
                                  onClick={() => approveAd(ad.id)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-500 border-red-300 hover:bg-red-50"
                                  onClick={() => rejectAd(ad.id)}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {ad.status === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => pauseAd(ad.id)}
                              >
                                <Pause className="h-4 w-4 mr-1" />
                                Pause
                              </Button>
                            )}
                            {(ad.status === 'paused' || ad.status === 'expired') && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600"
                                onClick={() => activateAd(ad.id)}
                              >
                                <Play className="h-4 w-4 mr-1" />
                                Activate
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => deleteAd(ad.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* QUOTES TAB */}
            <TabsContent value="quotes">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Quote Management</h2>
                  <p className="text-sm text-slate-600">Create, edit, and manage marketplace quotes</p>
                </div>
                <Button
                  onClick={() => {
                    setEditingQuote(null);
                    setQuoteForm({
                      title: '', origin: '', destination: '', cargo_type: 'general',
                      transport_mode: 'sea', currency: 'USD', base_rate: '', transit_time: '',
                      validity_days: '30', notes: '', status: 'published'
                    });
                    setShowQuoteForm(true);
                  }}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Quote
                </Button>
              </div>

              <div className="space-y-4">
                {quotes.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-slate-500">
                      <DollarSign className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      No quotes yet. Click "Create Quote" to add one.
                    </CardContent>
                  </Card>
                ) : (
                  quotes.map((quote) => (
                    <Card key={quote.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold">{quote.title || 'Untitled Quote'}</h3>
                              <Badge className={
                                quote.status === 'published' ? 'bg-green-100 text-green-700' :
                                quote.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                                quote.status === 'declined' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }>
                                {quote.status}
                              </Badge>
                              <Badge variant="outline">{quote.transport_mode}</Badge>
                              {quote.is_marketplace && (
                                <Badge className="bg-purple-100 text-purple-700">Marketplace</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">
                              {quote.origin} → {quote.destination}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>By: {quote.company_name || 'Admin'}</span>
                              <span>{quote.currency} {quote.total_price?.toLocaleString() || quote.base_rate?.toLocaleString()}</span>
                              {quote.transit_time && <span>Transit: {quote.transit_time}</span>}
                              <span>Created: {new Date(quote.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => editQuote(quote)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => deleteQuote(quote.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* RFQS TAB */}
            <TabsContent value="rfqs">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">RFQ Management</h2>
                  <p className="text-sm text-slate-600">Create, edit, and manage all RFQs on the platform</p>
                </div>
                <Button
                  onClick={() => {
                    setEditingRfq(null);
                    setRfqForm({
                      title: '', cargo_type: 'general', origin: '', destination: '',
                      weight: '', volume: '', deadline: '', budget: '', description: '', status: 'open'
                    });
                    setShowRfqForm(true);
                  }}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create RFQ
                </Button>
              </div>

              <div className="space-y-4">
                {rfqs.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-slate-500">
                      <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      No RFQs submitted yet
                    </CardContent>
                  </Card>
                ) : (
                  rfqs.map((rfq) => (
                    <Card key={rfq.id} className={rfq.status === 'flagged' ? 'border-orange-300' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">{rfq.title || 'Untitled RFQ'}</h3>
                              {getRfqStatusBadge(rfq.status)}
                              {rfq.is_public && (
                                <Badge variant="outline" className="text-xs">Public</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {rfq.origin} → {rfq.destination}
                              </span>
                              <span>{rfq.cargo_type}</span>
                              {rfq.weight && <span>{rfq.weight} kg</span>}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              <span className="font-medium">Company:</span> {rfq.company_name}
                            </p>
                            {rfq.description && (
                              <p className="text-sm text-slate-500 line-clamp-2">{rfq.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                              <span>Created: {new Date(rfq.created_at).toLocaleDateString()}</span>
                              <span>{rfq.quotes_count || 0} quotes received</span>
                              <span>{rfq.views_count || 0} views</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Select
                              value={rfq.status}
                              onValueChange={(value) => updateRfqStatus(rfq.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                                <SelectItem value="awarded">Awarded</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                                <SelectItem value="flagged">Flagged</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/rfqs/${rfq.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => editRfq(rfq)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => deleteRfq(rfq.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* News Preview Modal */}
          <Dialog open={!!previewNews} onOpenChange={() => setPreviewNews(null)}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">News Article Preview</DialogTitle>
              </DialogHeader>
              {previewNews && (
                <div className="pt-4">
                  {/* Preview as users will see it */}
                  <article className="bg-white rounded-lg overflow-hidden">
                    {previewNews.featured_image && (
                      <div className="w-full h-64 bg-slate-100">
                        <img 
                          src={previewNews.featured_image} 
                          alt={previewNews.title}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="capitalize">{previewNews.category?.replace('_', ' ') || 'News'}</Badge>
                        {previewNews.tags?.length > 0 && previewNews.tags.map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                      <h1 className="text-3xl font-bold text-ocean-900 mb-4">{previewNews.title}</h1>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {new Date(previewNews.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'long', day: 'numeric' 
                          })}
                        </span>
                        <span>CTC Cargo Network</span>
                      </div>
                      {previewNews.excerpt && (
                        <p className="text-lg text-slate-600 mb-6 italic border-l-4 border-cyan-500 pl-4">
                          {previewNews.excerpt}
                        </p>
                      )}
                      <div className="prose prose-slate max-w-none">
                        {previewNews.content?.split('\n').map((paragraph, i) => (
                          <p key={i} className="mb-4 text-slate-700 leading-relaxed">{paragraph}</p>
                        ))}
                      </div>
                      {previewNews.attachments?.length > 0 && (
                        <div className="mt-6 pt-6 border-t">
                          <h3 className="font-semibold mb-3">Attachments</h3>
                          <div className="flex flex-wrap gap-3">
                            {previewNews.attachments.map((att, i) => (
                              <a 
                                key={i} 
                                href={att.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm"
                              >
                                <File className="h-4 w-4" />
                                {att.filename}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </article>
                  {previewNews.published && (
                    <div className="mt-4 pt-4 border-t flex justify-end">
                      <Button
                        onClick={() => window.open(`/news/${previewNews.id}`, '_blank')}
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Full Article
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Event Preview Modal */}
          <Dialog open={!!previewEvent} onOpenChange={() => setPreviewEvent(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">Event Preview</DialogTitle>
              </DialogHeader>
              {previewEvent && (
                <div className="pt-4">
                  <div className="bg-gradient-to-br from-cyan-500 to-ocean-600 rounded-t-lg p-6 text-white">
                    <Badge className="bg-white/20 text-white mb-3 capitalize">{previewEvent.event_type}</Badge>
                    <h1 className="text-2xl font-bold mb-2">{previewEvent.title}</h1>
                    <div className="flex items-center gap-4 text-cyan-100">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(previewEvent.start_time).toLocaleDateString('en-US', { 
                          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white border border-t-0 rounded-b-lg p-6">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Start Time</p>
                        <p className="font-semibold">{new Date(previewEvent.start_time).toLocaleTimeString('en-US', { 
                          hour: '2-digit', minute: '2-digit' 
                        })}</p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">End Time</p>
                        <p className="font-semibold">{new Date(previewEvent.end_time).toLocaleTimeString('en-US', { 
                          hour: '2-digit', minute: '2-digit' 
                        })}</p>
                      </div>
                    </div>
                    {previewEvent.location && (
                      <div className="flex items-center gap-2 mb-4 text-slate-600">
                        <MapPin className="h-4 w-4 text-cyan-500" />
                        <span>{previewEvent.location}</span>
                      </div>
                    )}
                    <div className="mb-6">
                      <h3 className="font-semibold mb-2">About This Event</h3>
                      <p className="text-slate-600 leading-relaxed">{previewEvent.description}</p>
                    </div>
                    {previewEvent.max_attendees && (
                      <div className="bg-cyan-50 p-4 rounded-lg mb-6">
                        <div className="flex items-center justify-between">
                          <span className="text-cyan-700">Spots Available</span>
                          <span className="font-bold text-cyan-800">
                            {previewEvent.max_attendees - (previewEvent.registered_count || 0)} / {previewEvent.max_attendees}
                          </span>
                        </div>
                        <div className="w-full bg-cyan-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-cyan-600 h-2 rounded-full" 
                            style={{ width: `${((previewEvent.registered_count || 0) / previewEvent.max_attendees) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    {previewEvent.registration_url && (
                      <a
                        href={previewEvent.registration_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center bg-cyan-500 hover:bg-cyan-600 text-white py-3 rounded-lg font-semibold transition-colors"
                      >
                        Register Now
                      </a>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Ad Preview Modal */}
          <Dialog open={!!previewAd} onOpenChange={() => setPreviewAd(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl">Advertisement Preview</DialogTitle>
                <p className="text-sm text-slate-500">This is how the ad will appear to users as a popup</p>
              </DialogHeader>
              {previewAd && (
                <div className="pt-4">
                  {/* Simulate the actual popup appearance */}
                  <div className="bg-white rounded-xl shadow-2xl overflow-hidden border-2 border-slate-200">
                    {previewAd.image_url && (
                      <div className="w-full h-48 bg-slate-100">
                        <img 
                          src={toFullUrl(previewAd.image_url)} 
                          alt={previewAd.title}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h2 className="text-xl font-bold text-slate-900 mb-2">{previewAd.title}</h2>
                      {previewAd.content && (
                        <p className="text-slate-600 mb-4">{previewAd.content}</p>
                      )}
                      <p className="text-xs text-slate-400 mb-4">Sponsored by {previewAd.company_name}</p>
                      <div className="flex gap-3">
                        {previewAd.link_url && (
                          <a
                            href={previewAd.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center bg-cyan-500 hover:bg-cyan-600 text-white py-2.5 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            Learn More
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        <button className="flex-1 text-center bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 px-4 rounded-lg font-medium transition-colors">
                          Not Now
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Ad stats */}
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Ad Statistics</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-cyan-600">{previewAd.impressions || 0}</p>
                        <p className="text-xs text-slate-500">Impressions</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{previewAd.clicks || 0}</p>
                        <p className="text-xs text-slate-500">Clicks</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-600">
                          {previewAd.impressions ? ((previewAd.clicks || 0) / previewAd.impressions * 100).toFixed(1) : 0}%
                        </p>
                        <p className="text-xs text-slate-500">CTR</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* RFQ Form Dialog */}
          <Dialog open={showRfqForm} onOpenChange={setShowRfqForm}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingRfq ? 'Edit RFQ' : 'Create New RFQ'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleRfqSubmit} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>RFQ Title *</Label>
                  <Input
                    value={rfqForm.title}
                    onChange={(e) => setRfqForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., FCL Shipment Miami to Kingston"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Origin *</Label>
                    <Input
                      value={rfqForm.origin}
                      onChange={(e) => setRfqForm(prev => ({ ...prev, origin: e.target.value }))}
                      placeholder="e.g., Miami, FL"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Destination *</Label>
                    <Input
                      value={rfqForm.destination}
                      onChange={(e) => setRfqForm(prev => ({ ...prev, destination: e.target.value }))}
                      placeholder="e.g., Kingston, Jamaica"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cargo Type</Label>
                    <Select
                      value={rfqForm.cargo_type}
                      onValueChange={(v) => setRfqForm(prev => ({ ...prev, cargo_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Cargo</SelectItem>
                        <SelectItem value="fcl">FCL (Full Container)</SelectItem>
                        <SelectItem value="lcl">LCL (Less Container)</SelectItem>
                        <SelectItem value="air">Air Freight</SelectItem>
                        <SelectItem value="bulk">Bulk</SelectItem>
                        <SelectItem value="reefer">Refrigerated</SelectItem>
                        <SelectItem value="hazmat">Hazardous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={rfqForm.status}
                      onValueChange={(v) => setRfqForm(prev => ({ ...prev, status: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="awarded">Awarded</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Weight (kg)</Label>
                    <Input
                      type="number"
                      value={rfqForm.weight}
                      onChange={(e) => setRfqForm(prev => ({ ...prev, weight: e.target.value }))}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Volume (CBM)</Label>
                    <Input
                      type="number"
                      value={rfqForm.volume}
                      onChange={(e) => setRfqForm(prev => ({ ...prev, volume: e.target.value }))}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Budget (USD)</Label>
                    <Input
                      type="number"
                      value={rfqForm.budget}
                      onChange={(e) => setRfqForm(prev => ({ ...prev, budget: e.target.value }))}
                      placeholder="Optional"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={rfqForm.deadline}
                    onChange={(e) => setRfqForm(prev => ({ ...prev, deadline: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={rfqForm.description}
                    onChange={(e) => setRfqForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Detailed requirements for this RFQ..."
                    rows={4}
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowRfqForm(false)}>Cancel</Button>
                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600">
                    {editingRfq ? 'Update RFQ' : 'Create RFQ'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Software Form Dialog */}
          <Dialog open={showSoftwareForm} onOpenChange={setShowSoftwareForm}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSoftware ? 'Edit Software' : 'Add New Software'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSoftwareSubmit} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Software Name *</Label>
                  <Input
                    value={softwareForm.name}
                    onChange={(e) => setSoftwareForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., CargoTrack Pro"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={softwareForm.tagline}
                    onChange={(e) => setSoftwareForm(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="e.g., Streamline your freight operations"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={softwareForm.category}
                      onValueChange={(v) => setSoftwareForm(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freight_management">Freight Management</SelectItem>
                        <SelectItem value="warehouse">Warehouse Management</SelectItem>
                        <SelectItem value="customs">Customs & Compliance</SelectItem>
                        <SelectItem value="tracking">Tracking & Visibility</SelectItem>
                        <SelectItem value="accounting">Accounting & Finance</SelectItem>
                        <SelectItem value="crm">CRM</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Pricing Model</Label>
                    <Select
                      value={softwareForm.pricing}
                      onValueChange={(v) => setSoftwareForm(prev => ({ ...prev, pricing: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="freemium">Freemium</SelectItem>
                        <SelectItem value="subscription">Subscription</SelectItem>
                        <SelectItem value="one_time">One-time Purchase</SelectItem>
                        <SelectItem value="quote">Contact for Quote</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Website URL</Label>
                    <Input
                      value={softwareForm.website}
                      onChange={(e) => setSoftwareForm(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input
                      value={softwareForm.logo_url}
                      onChange={(e) => setSoftwareForm(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={softwareForm.description}
                    onChange={(e) => setSoftwareForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this software does..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Features (one per line)</Label>
                  <Textarea
                    value={softwareForm.features}
                    onChange={(e) => setSoftwareForm(prev => ({ ...prev, features: e.target.value }))}
                    placeholder="Real-time tracking&#10;API integration&#10;Mobile app"
                    rows={4}
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowSoftwareForm(false)}>Cancel</Button>
                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600">
                    {editingSoftware ? 'Update Software' : 'Add Software'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default AdminCMS;
